/**
 * GuestCleanupPage — iter197.4.
 *
 * Scrubs phantom `guest_xxxx` entries from `groups.coin_balances` and reverses
 * stale "no_show" fines that were issued before iter197.4 blocked guests
 * from absent-fining. Same selective-apply UX as Ledger Sanity (per-row
 * checkboxes + Apply on N).
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ScanSearch, AlertTriangle, CheckCircle2, Loader2, ShieldCheck,
  ScanLine, CheckSquare, Square, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

export default function GuestCleanupPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [excludedKeys, setExcludedKeys] = useState(() => new Set());

  useEffect(() => { if (report?.rows) setExcludedKeys(new Set()); }, [report]);

  const allKeys = useMemo(() => (report?.rows || []).map((r) => r.key), [report]);
  const selectedKeys = useMemo(
    () => allKeys.filter((k) => !excludedKeys.has(k)),
    [allKeys, excludedKeys],
  );
  const selectedTotal = useMemo(() => {
    const sel = new Set(selectedKeys);
    return (report?.rows || [])
      .filter((r) => sel.has(r.key))
      .reduce((s, r) => s + (r.linked_txn_total || 0), 0);
  }, [selectedKeys, report]);

  const toggleRow = useCallback((key) => {
    setExcludedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const selectAllNone = useCallback(() => {
    if (excludedKeys.size === 0) setExcludedKeys(new Set(allKeys));
    else setExcludedKeys(new Set());
  }, [allKeys, excludedKeys]);

  if (!user?.is_super_admin) {
    return (
      <div className="p-6 text-center" data-testid="guest-cleanup-forbidden">
        <ShieldCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold">Super Admin Only</h2>
      </div>
    );
  }

  const runScan = async ({ clearApplyResult = true } = {}) => {
    setScanning(true);
    if (clearApplyResult) setApplyResult(null);
    try {
      const res = await api.post('/super/guest-balance-cleanup', { dry_run: true });
      setReport(res.data);
      const n = res.data.total_rows || 0;
      if (n === 0) {
        toast.success('No guest balance pollution found — everything is clean.');
      } else {
        toast.warning(`${n} guest entry(ies) to scrub · ₹${(res.data.total_fines_to_reverse || 0).toLocaleString('en-IN')} to refund`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const runApply = async () => {
    if (!report || (report.total_rows || 0) === 0) {
      toast.error('Run Scan first');
      return;
    }
    if (selectedKeys.length === 0) {
      toast.error('Tick at least one row to apply the cleanup');
      return;
    }
    const msg = `Cleanup ${selectedKeys.length} guest entry(ies)?\n\n• Reverses ₹${selectedTotal.toLocaleString('en-IN')} of stale fines back to each group fund\n• Removes phantom guest_xxx keys from coin_balances\n• Audit-logged · idempotent\n• Cannot be undone (re-run scan finds nothing)`;
    if (!window.confirm(msg)) return;
    setApplying(true);
    try {
      const res = await api.post('/super/guest-balance-cleanup', {
        dry_run: false,
        keys: selectedKeys,
      });
      setApplyResult(res.data);
      toast.success(res.data.message || 'Cleanup applied');
      await runScan({ clearApplyResult: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const totalRows = report?.total_rows || 0;
  const hasReport = !!report && totalRows > 0;
  const noneSelected = selectedKeys.length === 0;
  const allSelected = excludedKeys.size === 0;

  return (
    <div className="p-6 max-w-2xl lg:max-w-none mx-auto space-y-5" data-testid="guest-cleanup-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin')}
          className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors"
          data-testid="guest-cleanup-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6 text-amber-500" /> Guest Balance Cleanup
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Scrub phantom guest_xxx entries from coin_balances and reverse stale fines.
          </p>
        </div>
      </div>

      <Card className="p-4 border-2 rounded-2xl bg-amber-500/5 border-amber-500/30">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-semibold">What does this do?</p>
            <p className="text-muted-foreground">
              Pre-iter197.4, external guests on a session could end up in absent_ids and
              get fined ₹500 as no-shows — which wrote their synthetic guest_xxxxxxxx id
              into coin_balances with negative balance. They then showed up as &quot;Former
              Members&quot;. This tool reverses each stale fine (refunds the group fund) and
              removes the phantom guest key. Idempotent.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Button
          size="lg"
          variant="outline"
          onClick={() => runScan()}
          disabled={scanning || applying}
          data-testid="guest-cleanup-scan-btn"
          className="rounded-2xl h-14"
        >
          {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
          {scanning ? 'Scanning…' : 'Scan + Show'}
        </Button>
        <Button
          size="lg"
          onClick={runApply}
          disabled={scanning || applying || totalRows === 0 || noneSelected}
          data-testid="guest-cleanup-apply-btn"
          className="rounded-2xl h-14 bg-amber-600 hover:bg-amber-700 disabled:opacity-40"
        >
          {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
          {applying
            ? 'Applying…'
            : hasReport
              ? `Apply on ${selectedKeys.length}`
              : 'Apply'}
        </Button>
      </div>

      {hasReport && (
        <Card className="p-3 border-2 rounded-2xl flex items-center gap-3 bg-card" data-testid="guest-cleanup-selection-summary">
          <button
            type="button"
            onClick={selectAllNone}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-muted transition-colors text-xs font-semibold"
            data-testid="guest-cleanup-toggle-all"
          >
            {allSelected
              ? <><CheckSquare className="w-4 h-4 text-amber-600" /> All selected</>
              : noneSelected
                ? <><Square className="w-4 h-4 text-muted-foreground" /> None selected</>
                : <><Square className="w-4 h-4 text-amber-600" /> Some excluded</>}
          </button>
          <div className="flex-1 text-xs text-muted-foreground">
            {selectedKeys.length} of {totalRows} entry(ies)
            <span className="mx-1.5 text-muted-foreground/50">·</span>
            <span className="font-mono text-amber-600 font-semibold">
              ₹{selectedTotal.toLocaleString('en-IN')}
            </span>
            {excludedKeys.size > 0 && (
              <span className="ml-1.5 text-amber-600 font-semibold">
                ({excludedKeys.size} excluded)
              </span>
            )}
          </div>
        </Card>
      )}

      {applyResult && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="p-4 border-2 rounded-2xl bg-green-500/10 border-green-500/40" data-testid="guest-cleanup-apply-result">
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div className="text-sm flex-1 min-w-0">
                <p className="font-semibold">{applyResult.message}</p>
                <p className="text-muted-foreground mt-1">
                  Applied: <span className="font-mono">{applyResult.applied}</span> · Refunded:{' '}
                  <span className="font-mono">₹{(applyResult.fund_refunded || 0).toLocaleString('en-IN')}</span>
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {report && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="guest-cleanup-report">
          <Card className={`p-4 border-2 rounded-2xl ${totalRows > 0 ? 'border-amber-500/40 bg-amber-500/5' : 'border-green-500/40 bg-green-500/5'}`}>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Entries</p>
                <p className="font-display text-xl font-bold text-amber-600" data-testid="guest-cleanup-stat-rows">{totalRows}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Phantom debt</p>
                <p className="font-display text-xl font-bold" data-testid="guest-cleanup-stat-balance">
                  ₹{Math.abs(report.total_phantom_balance || 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">To refund</p>
                <p className="font-display text-xl font-bold text-green-600" data-testid="guest-cleanup-stat-refund">
                  ₹{(report.total_fines_to_reverse || 0).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
            {report.message && (
              <p className="text-xs text-muted-foreground text-center mt-3 italic">{report.message}</p>
            )}
          </Card>

          {report.rows?.length > 0 && (
            <Card className="p-2 border-2 rounded-2xl" data-testid="guest-cleanup-rows">
              <h3 className="font-semibold text-sm px-2 pt-2 pb-2">Tap a row to toggle</h3>
              <div className="divide-y divide-border">
                {report.rows.map((r) => {
                  const isInc = !excludedKeys.has(r.key);
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => toggleRow(r.key)}
                      className={`w-full flex items-start gap-3 px-2 py-2.5 rounded-lg text-left transition-colors ${
                        isInc ? 'hover:bg-muted/50' : 'opacity-60 line-through'
                      }`}
                      data-testid={`guest-cleanup-row-${r.key.replace('|','-')}`}
                    >
                      <span className="shrink-0 mt-0.5">
                        {isInc
                          ? <CheckSquare className="w-4 h-4 text-amber-600" />
                          : <Square className="w-4 h-4 text-muted-foreground" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">
                          {r.guest_id} <span className="text-muted-foreground font-normal">in {r.group_name}</span>
                        </p>
                        <p className="text-xs text-muted-foreground font-mono">
                          Balance ₹{r.coin_balance} · {r.linked_txns.length} txn(s) · refund ₹{r.linked_txn_total}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}
        </motion.div>
      )}

      {!report && !scanning && (
        <Card className="p-8 border-2 border-dashed rounded-2xl text-center">
          <ScanSearch className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Tap <strong>Scan + Show</strong> to begin.
          </p>
        </Card>
      )}
    </div>
  );
}
